import * as Yup from "yup";

import { createForm } from "../../forms";
import { MAX_ENTRY_DESC_LENGTH } from "../../modules/achPayments/ACHPayment.form";
import {
  baseBulkFormSchema,
  basePaymentRowSchema,
} from "../bulkPayments/bulkBase";

import { MAX_BULK_TEMPLATE_TITLE_LENGTH } from "./consts";

import type { TypeSafeReactLocalization } from "../../../composites";
import type { ACHPaymentFormBaseType } from "../../modules/achPayments/ACHPayment.form";
import type { BasePaymentRowType } from "../bulkPayments/bulkBase";

export type BulkTemplatePaymentRowType = BasePaymentRowType & {
  speedStatus?: "instant" | "sameDay" | "standard" | "";
};

export type BulkTemplateFormType = ACHPaymentFormBaseType & {
  title: string;
  payments: BulkTemplatePaymentRowType[];
  groupPosting: boolean;
  description?: string;
  allowExpedite?: boolean;
  changeLog?: string;
  versionCount?: number;
  groupId?: API.BulkTemplateId;
};

export const MAX_CHANGE_LOG_LENGTH = 250;

export const bulkTemplateSchema = (l10n: TypeSafeReactLocalization) =>
  baseBulkFormSchema(l10n).concat(
    Yup.object().shape({
      title: Yup.string()
        .required(l10n.getString("form-generic-validation-required"))
        .max(
          MAX_BULK_TEMPLATE_TITLE_LENGTH,
          l10n.getString("form-generic-validation-limit-characters", {
            count: MAX_BULK_TEMPLATE_TITLE_LENGTH,
          }),
        ),
      secCode: Yup.string().optional(),
      entryDesc: Yup.string().max(MAX_ENTRY_DESC_LENGTH).optional(),
      allowExpedite: Yup.boolean().optional(),
    }),
  );

export const templatePaymentsSchema = (l10n: TypeSafeReactLocalization) =>
  Yup.object().shape({
    payments: Yup.array()
      .of(
        basePaymentRowSchema(l10n).shape({
          amount: Yup.number()
            .min(0, l10n.getString("form-generic-validation-positive-number"))
            .optional()
            .default(0),
        }),
      )
      .min(1, l10n.getString("bulk-ach-form-error-payment")),
  });

export const templateVersionChangeLogSchema = (
  l10n: TypeSafeReactLocalization,
) =>
  Yup.object().shape({
    changeLog: Yup.string()
      .trim()
      .required(l10n.getString("form-generic-validation-required"))
      .max(
        MAX_CHANGE_LOG_LENGTH,
        l10n.getString("form-generic-validation-limit-characters", {
          count: MAX_CHANGE_LOG_LENGTH,
        }),
      ),
  });

export const TemplateRowInitialValue: BulkTemplatePaymentRowType = {
  recipient: undefined,
  description: "",
  amount: 0,
  transactionType: "send",
};

export const initialValues: BulkTemplateFormType = {
  title: "",
  fromAccount: "",
  secCode: "",
  memo: "",
  entryDesc: "",
  payments: [{ ...TemplateRowInitialValue }],
  groupPosting: false,
  allowExpedite: undefined,
  changeLog: "",
};

export const BulkTemplateForm = createForm({
  initialValues,
  translateValidationSchema: bulkTemplateSchema,
});
